// AppHeader.tsx
import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api from "../api.js";
import "../style/css/AppHeader.css";

// Types
interface Clinic {
  id: string;
  name: string;
  address: string;
}

interface UserSettings {
  language: string;
  timezone: string;
  notifications: boolean;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatarUrl: string;
  role: string;
  specialization: string;
  clinic: Clinic;
  permissions: string[];
  settings: UserSettings;
}

interface NavItem {
  label: string;
  icon: string;
  href: string;
  permission?: string;
  badge?: string;
}

// Default fallback navigation (in case API fails)
const defaultNavItems: NavItem[] = [
  {
    label: "Agenda",
    icon: "calendar-alt",
    href: "/dashboard",
    permission: "view_calendar",
  },
  {
    label: "Citas",
    icon: "user-clock",
    href: "/appointments",
    permission: "manage_appointments",
  },
  {
    label: "Estadísticas",
    icon: "chart-line",
    href: "/stats",
    permission: "view_reports",
  },
  {
    label: "Pacientes",
    icon: "users",
    href: "/patients",
    permission: "manage_patients",
  },
  {
    label: "Servicios",
    icon: "hand-holding-medical",
    href: "/services",
    permission: "manage_services",
  },
  {
    label: "Sedes/Oficinas",
    icon: "building",
    href: "/offices",
    permission: "manage_offices",
  },
];

// Cache keys
const PROFILE_CACHE_KEY = "userProfile";
const NAVIGATION_CACHE_KEY = "userNavigation";
const CACHE_DURATION = 2; // 2 hours
// const CACHE_DURATION = 2;
export function AppHeader() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [navItems, setNavItems] = useState<NavItem[]>(defaultNavItems);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Check if cache is valid
  const isCacheValid = (cachedData: string | null): boolean => {
    if (!cachedData) return false;

    try {
      const { timestamp } = JSON.parse(cachedData);
      return Date.now() - timestamp < CACHE_DURATION;
    } catch {
      return false;
    }
  };

  // Get data from cache
  const getCachedData = <T,>(key: string): T | null => {
    const cached = localStorage.getItem(key);
    if (isCacheValid(cached)) {
      return JSON.parse(cached!).data;
    }
    return null;
  };

  // Set data to cache
  const setCachedData = (key: string, data: any) => {
    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );
  };

  // Clear cache on logout
  const clearCache = () => {
    localStorage.removeItem(PROFILE_CACHE_KEY);
    localStorage.removeItem(NAVIGATION_CACHE_KEY);
    setUserProfile(null);
    setNavItems(defaultNavItems);
  };

  // Fetch user profile from API
  const fetchUserProfile = async (): Promise<UserProfile | null> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "/api";
      const response = await api.get(`/profiles/`);

      if (response) {
        const profileData: UserProfile = await response.data;
        setCachedData(PROFILE_CACHE_KEY, profileData);
        return profileData;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Fetch navigation items from API
  const fetchNavigation = async (): Promise<NavItem[] | null> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "/api";
      const response = api.get(`/profiles/navigation/`);

      if (response) {
        const navData: NavItem[] = (await response).data;
        setCachedData(NAVIGATION_CACHE_KEY, navData);
        return navData;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Check user permissions for navigation items
  const filterNavItemsByPermission = (
    items: NavItem[],
    userPermissions: string[] = [],
  ): NavItem[] => {
    return items.filter(
      (item) => !item.permission || userPermissions.includes(item.permission),
    );
  };

  // Main data loading effect
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        clearCache();
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Load profile data (cache first, then API)
        let profileData = getCachedData<UserProfile>(PROFILE_CACHE_KEY);
        if (!profileData) {
          profileData = await fetchUserProfile();
        }
        setUserProfile(profileData);

        // Load navigation data (cache first, then API)
        let navigationData = getCachedData<NavItem[]>(NAVIGATION_CACHE_KEY);
        if (!navigationData) {
          navigationData = await fetchNavigation();
        }

        // Filter navigation items based on user permissions
        const userPermissions = profileData?.permissions || [];
        const filteredNavItems = navigationData
          ? filterNavItemsByPermission(navigationData, userPermissions)
          : filterNavItemsByPermission(defaultNavItems, userPermissions);

        setNavItems(filteredNavItems);
      } catch (error) {
        // Fallback to default navigation filtered by basic permissions
        const filteredDefaultNav = filterNavItemsByPermission(
          defaultNavItems,
          [],
        );
        setNavItems(filteredDefaultNav);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Display name logic
  const getDisplayName = (): string => {
    if (loading) return "Cargando...";
    if (userProfile?.name) return userProfile.name;
    if (user?.username) return user.username;
    return "Usuario";
  };

  // Get user role for display
  const getUserRole = (): string => {
    if (userProfile?.specialization && userProfile?.role) {
      return `${userProfile.specialization} - ${userProfile.role}`;
    }
    return userProfile?.role || "Usuario";
  };

  return (
    <header className="header-app">
      {/* Top Bar with Logo and Profile */}
      <div className="container">
        <nav className="navbar">
          <div className="logo">
            <NavLink to="/" className="logo-link-wrapper">
              <img
                src="/logo_resized.jpg"
                alt="Cara de agenda"
                className="logo-img"
              />
            </NavLink>
            <h3 style={{ fontSize: "1.7rem" }}>Cara de agenda</h3>
          </div>

          {/* Profile Dropdown */}
          <div className="profile-dropdown" ref={dropdownRef}>
            <button
              className="profile-btn"
              id="profile-toggle"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              disabled={loading}
            >
              <div className="profile-avatar">
                {userProfile?.avatarUrl ? (
                  <img
                    src={userProfile.avatarUrl}
                    alt={getDisplayName()}
                    className="avatar-img"
                  />
                ) : (
                  <img
                    src="/user_placeholder.svg"
                    alt="User avatar"
                    className="avatar-img" // Add this class
                  />
                )}
              </div>
              <div className="profile-info">
                <span className="profile-name">{getDisplayName()}</span>
                {/* <span className="profile-role">{getUserRole()}</span> */}
              </div>
              <i
                className={`fas fa-chevron-down ${
                  isDropdownOpen ? "rotated" : ""
                }`}
                style={{ fontSize: "0.7rem" }}
              ></i>
            </button>

            <div
              className={`dropdown-content ${isDropdownOpen ? "show" : ""}`}
              id="dropdown-menu"
            >
              <NavLink to="/profile" onClick={() => setIsDropdownOpen(false)}>
                <i className="fas fa-user-circle"></i> Mi Perfil
              </NavLink>
              <NavLink
                to="/profile/telegram_bot"
                onClick={() => setIsDropdownOpen(false)}
              >
                <i className="fas fa-user-circle"></i> Asistente virtual
              </NavLink>
              {userProfile?.clinic && (
                <div className="dropdown-clinic-info">
                  <i className="fas fa-building"></i>
                  <span>{userProfile.clinic.name}</span>
                </div>
              )}
              <a onClick={logout}>
                <i className="fas fa-sign-out-alt"></i> Cerrar Sesión
              </a>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Navigation Bar */}
      <nav className="main-nav-bar">
        <div className="container">
          <ul className="main-nav-list">
            {navItems.map((item) => (
              <li key={item.label} className="main-nav-item">
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "nav-active" : ""}`
                  }
                >
                  <div className="nav-icon">
                    <i className={`fas fa-${item.icon}`}></i>
                    {/* {item.badge && (
                      <span className="nav-badge">{item.badge}</span>
                    )} */}
                  </div>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
}

export default AppHeader;
